import type { Envelope } from '@/adapters/catalogv1.g';
import type {
  ActivityEvent,
  EntityMetadata,
  EntityPatchedEvent,
  EntityRemovedEvent,
  EventContext,
  EventMetadata,
  EventOutcome,
  JsonPatchOperation,
  LogEvent,
  SecuritySignal,
  Actor,
  Target,
} from '@/types/data/catalog-log-event';
import {
  DISCRIMINATOR_ENTITY_PATCHED,
  DISCRIMINATOR_ENTITY_REMOVED,
  DISCRIMINATOR_ENTITY_RETIRED,
} from '@/types/data/catalog-log-registry';

type Payload = Record<string, unknown>;

export type LegacyActivityLogRecord = {
  connector_id?: string;
  discriminator?: string;
  event_id?: string;
  event_ref?: string;
  payload?: unknown;
  space?: string;
  timestamp_unix_nano?: number;
};

export type CatalogLogEnvelope = Envelope & {
  $type?: string;
  content?: unknown;
};

/** Convert nanosecond unix timestamp to ISO string */
function nanoToIso(nanos: number | undefined): string {
  if (nanos === undefined || nanos === 0) return new Date(0).toISOString();
  return new Date(nanos / 1_000_000).toISOString();
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function extractTarget(raw: unknown): Target {
  const t = (raw ?? {}) as Payload;
  return {
    ref: str(t['ref']),
    type: str(t['type']),
    displayName:
      typeof t['display_name'] === 'string' ? t['display_name'] : undefined,
  };
}

function extractActor(raw: unknown): Actor {
  const a = (raw ?? {}) as Payload;
  const initiator = a['initiator'];
  return {
    ref: str(a['ref']),
    type: str(a['type']),
    displayName:
      typeof a['display_name'] === 'string' ? a['display_name'] : undefined,
    initiator:
      initiator && typeof initiator === 'object'
        ? extractActor(initiator)
        : undefined,
  };
}

function extractEntityMetadata(raw: unknown): EntityMetadata {
  const m = (raw ?? {}) as Payload;
  return {
    tenantId: str(m['tenant_id']),
    segmentId: str(m['segment_id']),
    space: str(m['space']),
    distinctId: str(m['distinct_id']),
    discriminator: str(m['discriminator']),
  };
}

function extractEventMetadata(raw: unknown): EventMetadata {
  const m = (raw ?? {}) as Payload;
  return {
    tenantId: str(m['tenant_id']),
    segmentId: str(m['segment_id']),
    space: str(m['space']),
    eventId: str(m['event_id']),
    discriminator: str(m['discriminator']),
  };
}

function extractContext(raw: unknown): EventContext {
  const c = (raw ?? {}) as Payload;
  return {
    ipAddress:
      typeof c['ip_address'] === 'string' ? c['ip_address'] : undefined,
    userAgent:
      typeof c['user_agent'] === 'string' ? c['user_agent'] : undefined,
    sessionId:
      typeof c['session_id'] === 'string' ? c['session_id'] : undefined,
    deviceRef:
      typeof c['device_ref'] === 'string' ? c['device_ref'] : undefined,
    locationRef:
      typeof c['location_ref'] === 'string' ? c['location_ref'] : undefined,
  };
}

function extractOutcome(raw: unknown): EventOutcome {
  const o = (raw ?? {}) as Payload;
  const validResults = ['success', 'failure', 'blocked', 'partial'] as const;
  const result = validResults.includes(
    o['result'] as (typeof validResults)[number]
  )
    ? (o['result'] as EventOutcome['result'])
    : 'success';
  return {
    action: str(o['action']),
    result,
    reason: typeof o['reason'] === 'string' ? o['reason'] : undefined,
  };
}

function extractSecurity(raw: unknown): SecuritySignal | undefined {
  if (!raw) return undefined;
  const s = raw as Payload;
  const validLevels = ['none', 'low', 'medium', 'high', 'critical'] as const;
  return {
    riskLevel: validLevels.includes(
      s['risk_level'] as (typeof validLevels)[number]
    )
      ? (s['risk_level'] as SecuritySignal['riskLevel'])
      : undefined,
    riskReasons: Array.isArray(s['risk_reasons'])
      ? (s['risk_reasons'] as unknown[]).map(String)
      : undefined,
    threatType:
      typeof s['threat_type'] === 'string' ? s['threat_type'] : undefined,
    confidence:
      typeof s['confidence'] === 'number' ? s['confidence'] : undefined,
  };
}

function extractPatch(raw: unknown): JsonPatchOperation[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((op) => {
      const o = (op ?? {}) as Payload;
      return {
        op: str(o['op']) as JsonPatchOperation['op'],
        path: str(o['path']),
        value: o['value'],
        from: typeof o['from'] === 'string' ? o['from'] : undefined,
        // old_value / oldValue: backend extension beyond RFC 6902 JSON Patch.
        oldValue: o['old_value'] ?? o['oldValue'],
      };
    })
    .filter((op) => !op.path.startsWith('/_'));
}

function isLegacyActivityLogRecord(
  value: LegacyActivityLogRecord | CatalogLogEnvelope
): value is LegacyActivityLogRecord {
  return 'payload' in value || 'timestamp_unix_nano' in value;
}

function extractConnectorId(payload: Payload): string {
  if (typeof payload['connector_id'] === 'string') {
    return payload['connector_id'];
  }
  const metadata = (payload['_metadata'] ?? {}) as Payload;
  return str(metadata['connector_id']);
}

export function extractLogEvent(
  rec: LegacyActivityLogRecord | CatalogLogEnvelope
): LogEvent {
  const legacy = isLegacyActivityLogRecord(rec);
  const payload = ((legacy ? rec.payload : rec.content) ?? {}) as Payload;
  const discriminator = legacy ? (rec.discriminator ?? '') : (rec.$type ?? '');
  const timestamp = legacy
    ? nanoToIso(rec.timestamp_unix_nano)
    : str(payload['timestamp']);
  const connectorId = legacy
    ? (rec.connector_id ?? '')
    : extractConnectorId(payload);
  const metadata = (payload['_metadata'] ?? {}) as Payload;
  const eventRef = legacy
    ? (rec.event_id ?? rec.event_ref ?? '')
    : str(
        metadata['event_id'],
        str(payload['event_id'], str(payload['event_ref']))
      );

  if (discriminator === DISCRIMINATOR_ENTITY_PATCHED) {
    const event: EntityPatchedEvent = {
      kind: 'entityPatched',
      connectorId,
      metadata: extractEntityMetadata(payload['_metadata']),
      eventRef,
      timestamp,
      target: extractTarget(payload['target']),
      patch: extractPatch(payload['patch']),
    };
    return event;
  }

  if (
    discriminator === DISCRIMINATOR_ENTITY_REMOVED ||
    discriminator === DISCRIMINATOR_ENTITY_RETIRED
  ) {
    const event: EntityRemovedEvent = {
      kind: 'entityRemoved',
      connectorId,
      metadata: extractEntityMetadata(payload['_metadata']),
      eventRef,
      timestamp,
      target: extractTarget(payload['target']),
      reason:
        typeof payload['reason'] === 'string' ? payload['reason'] : undefined,
    };
    return event;
  }

  const event: ActivityEvent = {
    kind: 'activity',
    connectorId,
    metadata: extractEventMetadata(payload['_metadata']),
    eventRef,
    timestamp,
    actor: extractActor(payload['actor']),
    target: extractTarget(payload['target']),
    context: extractContext(payload['context']),
    outcome: extractOutcome(payload['outcome']),
    security: extractSecurity(payload['security']),
  };
  return event;
}
